import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  ResourceList,
  ResourceItem,
  TextStyle,
  Button,
  Modal,
  FormLayout,
  TextField,
  Select,
  Stack,
  Badge,
  Banner,
  Checkbox,
  Tabs,
  TextContainer,
  Layout,
  Heading,
  Icon,
  Tooltip
} from '@shopify/polaris';
import { DeleteMajor, EditMajor, DuplicateMinor, QuestionMarkMajor } from '@shopify/polaris-icons';
import { useAuth } from '../../contexts/AuthContext';
import { useShop } from '../../contexts/ShopContext';
import {
  getAvailableAIModels,
  createAIModel,
  updateAIModel,
  deleteAIModel,
  getUserModelSettings,
  updateUserModelSettings
} from '../../services/aiModelService';
import { saveProviderApiKey } from '../../services/apiKeyService';

export default function AIModelManagement() {
  // State for AI models
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModelModal, setShowModelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // State for provider settings
  const [showProviderSettingsModal, setShowProviderSettingsModal] = useState(false);
  const [userSettings, setUserSettings] = useState({
    openai: { apiKey: '', baseUrl: 'https://api.openai.com/v1' },
    deepseek: { apiKey: '', baseUrl: 'https://api.deepseek.com/v1' },
    gemini: { apiKey: '', baseUrl: 'https://generativelanguage.googleapis.com/v1beta' },
    claude: { apiKey: '', baseUrl: 'https://api.anthropic.com/v1' }
  });
  
  // State for editing a model
  const [editingModel, setEditingModel] = useState({
    id: '',
    displayName: '',
    provider: 'openai',
    modelId: '',
    isEnabled: true,
    isDefault: false,
    creditCostPerOutput: 1,
    temperature: 0.7,
    maxTokens: 2000,
    type: 'text',
    maxImages: 1,
    apiConfig: {
      apiKey: '',
      baseUrl: ''
    }
  });
  
  // State for tab selection
  const [selectedTab, setSelectedTab] = useState(0);
  
  const { user } = useAuth();
  const { currentShop } = useShop();
  
  // Provider options
  const providerOptions = [
    { label: 'OpenAI', value: 'openai' },
    { label: 'DeepSeek', value: 'deepseek' },
    { label: 'Google Gemini', value: 'gemini' },
    { label: 'Anthropic Claude', value: 'claude' }
  ];
  
  // Common model options by provider
  const modelOptions = {
    openai: [
      { label: 'GPT-3.5 Turbo', value: 'gpt-3.5-turbo' },
      { label: 'GPT-4o Mini', value: 'gpt-4o-mini' },
      { label: 'GPT-4o', value: 'gpt-4o' },
      { label: 'GPT-4 Turbo', value: 'gpt-4-turbo' },
      { label: 'Custom', value: 'custom' }
    ],
    deepseek: [
      { label: 'DeepSeek Coder', value: 'deepseek-coder' },
      { label: 'DeepSeek Chat', value: 'deepseek-chat' },
      { label: 'DeepSeek LLM', value: 'deepseek-llm' },
      { label: 'Custom', value: 'custom' }
    ],
    gemini: [
      { label: 'Gemini 1.5 Pro', value: 'gemini-1.5-pro' },
      { label: 'Gemini 1.5 Flash', value: 'gemini-1.5-flash' },
      { label: 'Gemini 1.0 Pro', value: 'gemini-1.0-pro' },
      { label: 'Gemini 1.5 Pro Vision', value: 'gemini-1.5-pro-vision' },
      { label: 'Gemini 1.5 Flash Vision', value: 'gemini-1.5-flash-vision' },
      { label: 'Gemini Pro Vision', value: 'gemini-pro-vision' },
      { label: 'Custom', value: 'custom' }
    ],
    claude: [
      { label: 'Claude 4 Sonnet', value: 'claude-sonnet-4-20250514' },
      { label: 'Claude 3.7 Sonnet', value: 'claude-3-7-sonnet-latest' },
      { label: 'Claude 3.5 Sonnet', value: 'claude-3-5-sonnet-latest' },
      { label: 'Claude 3.5 Haiku', value: 'claude-3-5-haiku-latest' },
      { label: 'Claude 3 Opus', value: 'claude-3-opus-latest' },
      { label: 'Custom', value: 'custom' }
    ]
  };
  
  // Load AI models
  const loadModels = useCallback(async () => {
    try {
      setLoading(true);
      const availableModels = await getAvailableAIModels();
      setModels(availableModels);
    } catch (error) {
      console.error('Error loading AI models:', error);
      setError('Failed to load AI models');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Load user settings
  const loadUserSettings = useCallback(async () => {
    if (!user?.uid) return;
    
    try {
      const settings = await getUserModelSettings(user.uid);
      if (settings) {
        setUserSettings(prev => ({
          ...prev,
          ...settings
        }));
      }
    } catch (error) {
      console.error('Error loading user model settings:', error);
    }
  }, [user?.uid]);
  
  // Load data on component mount
  useEffect(() => {
    loadModels();
    loadUserSettings();
  }, [loadModels, loadUserSettings]);
  
  // Handle saving a model
  const handleSaveModel = async () => {
    try {
      // Validate required fields
      if (!editingModel.displayName || !editingModel.provider || !editingModel.modelId) {
        setError('Please fill in all required fields');
        return;
      }
      
      // Validate form
      if (!editingModel.displayName) {
        setError('Display name is required');
        return;
      }
      
      if (!editingModel.modelId) {
        setError('Model ID is required');
        return;
      }
      
      // Validate maxImages for vision models
      if (editingModel.type === 'vision' && (!editingModel.maxImages || editingModel.maxImages < 1)) {
        setError('Max images must be at least 1 for vision models');
        return;
      }
      
      // Create or update the model
      let result;
      if (editingModel.id) {
        result = await updateAIModel(editingModel.id, editingModel);
        setSuccess(`Model ${editingModel.displayName} updated successfully`);
      } else {
        result = await createAIModel(editingModel);
        setSuccess(`Model ${editingModel.displayName} created successfully`);
      }
      
      // Refresh the model list
      await loadModels();
      
      // Close the modal
      setShowModelModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving AI model:', error);
      setError(`Failed to save model: ${error.message}`);
    }
  };
  
  // Handle deleting a model
  const handleDeleteModel = async () => {
    try {
      if (!modelToDelete) return;
      
      await deleteAIModel(modelToDelete.id);
      setSuccess(`Model "${modelToDelete.displayName}" deleted successfully`);
      setShowDeleteModal(false);
      setModelToDelete(null);
      loadModels();
    } catch (error) {
      console.error('Error deleting AI model:', error);
      setError(`Failed to delete model: ${error.message}`);
    }
  };
  
  // Handle saving provider settings
  const handleSaveProviderSettings = async () => {
    try {
      if (!user?.uid) {
        setError('User not authenticated');
        return;
      }
      
      // Save the OpenAI API key and base URL to centralized location
      if (userSettings.openai?.apiKey) {
        await saveProviderApiKey('openai', userSettings.openai.apiKey, userSettings.openai.baseUrl);
      }
      
      // Save the DeepSeek API key and base URL to centralized location
      if (userSettings.deepseek?.apiKey) {
        await saveProviderApiKey('deepseek', userSettings.deepseek.apiKey, userSettings.deepseek.baseUrl);
      }
      
      // Save the Gemini API key and base URL to centralized location
      if (userSettings.gemini?.apiKey) {
        await saveProviderApiKey('gemini', userSettings.gemini.apiKey, userSettings.gemini.baseUrl);
      }
      
      // Save the Claude API key and base URL to centralized location
      if (userSettings.claude?.apiKey) {
        await saveProviderApiKey('claude', userSettings.claude.apiKey, userSettings.claude.baseUrl);
      }
      
      await updateUserModelSettings(user.uid, userSettings);
      setSuccess('Provider settings saved successfully');
      setShowProviderSettingsModal(false);
    } catch (error) {
      console.error('Error saving provider settings:', error);
      setError(`Failed to save provider settings: ${error.message}`);
    }
  };
  
  // Handle provider change
  const handleProviderChange = (provider) => {
    // Set default base URL based on provider
    let baseUrl = '';
    switch (provider) {
      case 'openai':
        baseUrl = 'https://api.openai.com/v1';
        break;
      case 'deepseek':
        baseUrl = 'https://api.deepseek.com/v1';
        break;
      case 'gemini':
        baseUrl = 'https://generativelanguage.googleapis.com/v1beta';
        break;
      case 'claude':
        baseUrl = 'https://api.anthropic.com/v1';
        break;
      default:
        baseUrl = '';
    }
    
    setEditingModel(prev => ({
      ...prev,
      provider,
      modelId: '', // Reset model ID when provider changes
      apiConfig: {
        ...prev.apiConfig,
        baseUrl: baseUrl
      }
    }));
  };
  
  // Reset the form
  const resetForm = () => {
    setEditingModel({
      id: '',
      displayName: '',
      provider: 'openai',
      modelId: '',
      isEnabled: true,
      isDefault: false,
      creditCostPerOutput: 1,
      temperature: 0.7,
      maxTokens: 2000,
      type: 'text',
      maxImages: 1,
      apiConfig: {
        apiKey: '',
        baseUrl: ''
      }
    });
    setError('');
  };
  
  // Handle editing a model
  const handleEditModel = (model) => {
    // Set default base URL based on provider if not already set
    let baseUrl = model.apiConfig?.baseUrl || '';
    if (!baseUrl) {
      switch (model.provider) {
        case 'openai':
          baseUrl = 'https://api.openai.com/v1';
          break;
        case 'deepseek':
          baseUrl = 'https://api.deepseek.com/v1';
          break;
        case 'gemini':
          baseUrl = 'https://generativelanguage.googleapis.com/v1beta';
          break;
        case 'claude':
          baseUrl = 'https://api.anthropic.com/v1';
          break;
        default:
          baseUrl = '';
      }
    }
    
    setEditingModel({
      ...model,
      apiConfig: {
        apiKey: model.apiConfig?.apiKey || '',
        baseUrl: baseUrl
      }
    });
    setShowModelModal(true);
  };
  
  // Handle duplicating a model
  const handleDuplicateModel = (model) => {
    setEditingModel({
      ...model,
      id: '',
      displayName: `${model.displayName} (Copy)`,
      isDefault: false
    });
    setShowModelModal(true);
  };
  
  // Handle model ID selection
  const handleModelIdChange = (value) => {
    if (value === 'custom') {
      // Keep the current value if it's a custom model
      return;
    }
    
    setEditingModel(prev => ({
      ...prev,
      modelId: value
    }));
  };
  
  // Render tabs
  const tabs = [
    {
      id: 'models',
      content: 'AI Models',
      accessibilityLabel: 'AI Models',
      panelID: 'models-panel',
    },
    {
      id: 'provider-settings',
      content: 'Provider Settings',
      accessibilityLabel: 'Provider Settings',
      panelID: 'provider-settings-panel',
    },
  ];
  
  return (
    <Page 
      title="AI Model Management"
      subtitle="Configure AI models and provider settings for your application"
      primaryAction={{
        content: 'Add AI Model',
        onAction: () => {
          resetForm();
          setShowModelModal(true);
        }
      }}
      secondaryActions={[
        {
          content: 'Provider Settings',
          onAction: () => setShowProviderSettingsModal(true)
        }
      ]}
    >
      {error && (
        <Banner status="critical" onDismiss={() => setError('')}>
          <p>{error}</p>
        </Banner>
      )}
      
      {success && (
        <Banner status="success" onDismiss={() => setSuccess('')}>
          <p>{success}</p>
        </Banner>
      )}
      
      <Tabs
        tabs={tabs}
        selected={selectedTab}
        onSelect={(index) => setSelectedTab(index)}
      />
      
      {selectedTab === 0 && (
        <Card>
          <ResourceList
            resourceName={{ singular: 'AI Model', plural: 'AI Models' }}
            items={models}
            loading={loading}
            renderItem={(model) => {
              const { id, displayName, provider, modelId, isEnabled, isDefault, creditCostPerOutput, type } = model;
              
              const shortcutActions = [
                {
                  content: 'Edit',
                  icon: EditMajor,
                  onAction: () => handleEditModel(model)
                },
                {
                  content: 'Duplicate',
                  icon: DuplicateMinor,
                  onAction: () => handleDuplicateModel(model)
                },
                {
                  content: 'Delete',
                  icon: DeleteMajor,
                  destructive: true,
                  onAction: () => {
                    setModelToDelete(model);
                    setShowDeleteModal(true);
                  }
                }
              ];
              
              return (
                <ResourceItem
                  id={id}
                  shortcutActions={shortcutActions}
                  accessibilityLabel={`View details for ${displayName}`}
                >
                  <Stack distribution="equalSpacing" alignment="center">
                    <Stack vertical spacing="extraTight">
                      <TextStyle variation="strong">{displayName}</TextStyle>
                      <TextStyle variation="subdued">{providerOptions.find(p => p.value === provider)?.label || provider} / {modelId}</TextStyle>
                    </Stack>
                    
                    <Stack spacing="tight">
                      {type === 'vision' 
                        ? <Badge status="info">👁️ Vision</Badge>
                        : <Badge>📝 Text</Badge>
                      }
                      {isDefault && <Badge status="success">Default</Badge>}
                      {!isEnabled && <Badge status="warning">Disabled</Badge>}
                      <Badge>{creditCostPerOutput} credit{creditCostPerOutput !== 1 ? 's' : ''} per output</Badge>
                    </Stack>
                  </Stack>
                </ResourceItem>
              );
            }}
            emptyState={models.length === 0 && !loading ? (
              <Stack vertical alignment="center" distribution="center" spacing="loose">
                <TextStyle variation="subdued">No AI models configured yet</TextStyle>
                <Button primary onClick={() => {
                  resetForm();
                  setShowModelModal(true);
                }}>
                  Add your first AI model
                </Button>
              </Stack>
            ) : null}
          />
        </Card>
      )}
      
      {selectedTab === 1 && (
        <Layout>
          <Layout.Section>
            <Card title="Provider Settings" sectioned>
              <TextContainer>
                <p>Configure API keys and base URLs for each AI provider. These settings will be used for all models of the respective provider.</p>
              </TextContainer>
              
              <Button primary onClick={() => setShowProviderSettingsModal(true)}>
                Edit Provider Settings
              </Button>
            </Card>
          </Layout.Section>
          
          <Layout.Section secondary>
            <Card title="About Provider Settings" sectioned>
              <TextContainer>
                <p>Provider settings are used to configure the connection to different AI providers:</p>
                <ul>
                  <li><strong>API Keys:</strong> Required for authentication with the provider</li>
                  <li><strong>Base URLs:</strong> The base endpoint for API requests</li>
                </ul>
                <p>These settings are stored securely and are only accessible by administrators.</p>
              </TextContainer>
            </Card>
          </Layout.Section>
        </Layout>
      )}
      
      {/* Model Modal */}
      <Modal
        open={showModelModal}
        onClose={() => {
          setShowModelModal(false);
          resetForm();
        }}
        title={editingModel.id ? 'Edit AI Model' : 'Add AI Model'}
        primaryAction={{
          content: editingModel.id ? 'Save Changes' : 'Add Model',
          onAction: handleSaveModel
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowModelModal(false);
              resetForm();
            }
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Display Name"
              value={editingModel.displayName}
              onChange={(value) => setEditingModel(prev => ({ ...prev, displayName: value }))}
              error={!editingModel.displayName ? 'Display name is required' : ''}
              helpText="The name that will be displayed to users"
              autoComplete="off"
            />
            
            <Select
              label="Provider"
              options={providerOptions}
              value={editingModel.provider}
              onChange={handleProviderChange}
              helpText="The AI provider for this model"
            />
            
            <FormLayout.Group>
              <Select
                label="Model ID"
                options={modelOptions[editingModel.provider] || []}
                value={modelOptions[editingModel.provider]?.find(m => m.value === editingModel.modelId) ? editingModel.modelId : 'custom'}
                onChange={handleModelIdChange}
                helpText="Select a predefined model or choose 'Custom' to enter a custom model ID"
              />
              
              {(!modelOptions[editingModel.provider]?.find(m => m.value === editingModel.modelId) || editingModel.modelId === 'custom') && (
                <TextField
                  label="Custom Model ID"
                  value={editingModel.modelId === 'custom' ? '' : editingModel.modelId}
                  onChange={(value) => setEditingModel(prev => ({ ...prev, modelId: value }))}
                  error={!editingModel.modelId ? 'Model ID is required' : ''}
                  helpText="The model identifier used by the provider"
                  autoComplete="off"
                />
              )}
            </FormLayout.Group>
            
            <TextField
              label="Credit Cost Per Output Field"
              type="number"
              value={editingModel.creditCostPerOutput.toString()}
              onChange={(value) => setEditingModel(prev => ({ ...prev, creditCostPerOutput: value }))}
              helpText={
                <span>
                  Number of credits to deduct per output field. More powerful models typically cost more.
                  <Tooltip content="For example, if a template has 3 output fields and this model costs 2 credits per output, it will cost 6 credits total to run the template.">
                    <span style={{ marginLeft: '4px', cursor: 'help' }}>
                      <Icon source={QuestionMarkMajor} color="base" />
                    </span>
                  </Tooltip>
                </span>
              }
              min={1}
              step={1}
            />
            
            <FormLayout.Group>
              <TextField
                label="Temperature"
                type="number"
                value={editingModel.temperature.toString()}
                onChange={(value) => setEditingModel(prev => ({ ...prev, temperature: value }))}
                helpText="Controls randomness (0.0-1.0)"
                min={0}
                max={1}
                step={0.1}
              />
              
              <TextField
                label="Max Tokens"
                type="number"
                value={editingModel.maxTokens.toString()}
                onChange={(value) => setEditingModel(prev => ({ ...prev, maxTokens: value }))}
                helpText="Maximum response length"
                min={100}
                step={100}
              />
            </FormLayout.Group>
            
            <FormLayout.Group>
              <TextField
                label="API Key"
                value={editingModel.apiConfig.apiKey}
                onChange={(value) => setEditingModel(prev => ({
                  ...prev,
                  apiConfig: {
                    ...prev.apiConfig,
                    apiKey: value
                  }
                }))}
                type="password"
                autoComplete="off"
                helpText="API key for the model"
              />
              
              <TextField
                label="Base URL"
                value={editingModel.apiConfig.baseUrl}
                onChange={(value) => setEditingModel(prev => ({
                  ...prev,
                  apiConfig: {
                    ...prev.apiConfig,
                    baseUrl: value
                  }
                }))}
                autoComplete="off"
                helpText="Base URL for API requests"
              />
            </FormLayout.Group>
            
            <FormLayout.Group>
              <Select
                label="Model Type"
                options={[
                  { label: 'Text', value: 'text' },
                  { label: 'Vision', value: 'vision' }
                ]}
                value={editingModel.type || 'text'}
                onChange={(value) => setEditingModel(prev => ({ ...prev, type: value }))}
                helpText="Text models process text only. Vision models can process images."
              />
              
              {editingModel.type === 'vision' && (
                <TextField
                  label="Max Images"
                  type="number"
                  value={editingModel.maxImages?.toString() || '1'}
                  onChange={(value) => setEditingModel(prev => ({ ...prev, maxImages: parseInt(value) || 1 }))}
                  helpText="Maximum number of images this model can process at once"
                  min={1}
                  step={1}
                />
              )}
            </FormLayout.Group>
            
            <FormLayout.Group>
              <Checkbox
                label="Enabled"
                checked={editingModel.isEnabled}
                onChange={(checked) => setEditingModel(prev => ({ ...prev, isEnabled: checked }))}
                helpText="If disabled, this model cannot be used"
              />
              
              <Checkbox
                label="Default Model"
                checked={editingModel.isDefault}
                onChange={(checked) => setEditingModel(prev => ({ ...prev, isDefault: checked }))}
                helpText="Set as the default model for new templates"
              />
            </FormLayout.Group>
          </FormLayout>
        </Modal.Section>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setModelToDelete(null);
        }}
        title="Delete AI Model"
        primaryAction={{
          content: 'Delete',
          destructive: true,
          onAction: handleDeleteModel
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowDeleteModal(false);
              setModelToDelete(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>Are you sure you want to delete the model "{modelToDelete?.displayName}"? This action cannot be undone.</p>
            {modelToDelete?.isDefault && (
              <Banner status="warning">
                <p>This is currently set as the default model. If deleted, you will need to set a new default model.</p>
              </Banner>
            )}
          </TextContainer>
        </Modal.Section>
      </Modal>
      
      {/* Provider Settings Modal */}
      <Modal
        open={showProviderSettingsModal}
        onClose={() => setShowProviderSettingsModal(false)}
        title="Provider Settings"
        primaryAction={{
          content: 'Save Settings',
          onAction: handleSaveProviderSettings
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowProviderSettingsModal(false)
          }
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <Heading>OpenAI</Heading>
            <FormLayout.Group>
              <TextField
                label="API Key"
                value={userSettings.openai.apiKey}
                onChange={(value) => setUserSettings(prev => ({
                  ...prev,
                  openai: {
                    ...prev.openai,
                    apiKey: value
                  }
                }))}
                type="password"
                autoComplete="off"
                helpText="Your OpenAI API key"
              />
            </FormLayout.Group>
            
            <TextField
              label="Base URL"
              value={userSettings.openai.baseUrl}
              onChange={(value) => setUserSettings(prev => ({
                ...prev,
                openai: {
                  ...prev.openai,
                  baseUrl: value
                }
              }))}
              autoComplete="off"
              helpText="Base URL for OpenAI API requests (default: https://api.openai.com/v1)"
            />
            
            <Heading>DeepSeek</Heading>
            <FormLayout.Group>
              <TextField
                label="API Key"
                value={userSettings.deepseek.apiKey}
                onChange={(value) => setUserSettings(prev => ({
                  ...prev,
                  deepseek: {
                    ...prev.deepseek,
                    apiKey: value
                  }
                }))}
                type="password"
                autoComplete="off"
                helpText="Your DeepSeek API key"
              />
            </FormLayout.Group>
            
            <TextField
              label="Base URL"
              value={userSettings.deepseek.baseUrl}
              onChange={(value) => setUserSettings(prev => ({
                ...prev,
                deepseek: {
                  ...prev.deepseek,
                  baseUrl: value
                }
              }))}
              autoComplete="off"
              helpText="Base URL for DeepSeek API requests (default: https://api.deepseek.com/v1)"
            />
            
            <Heading>Google Gemini</Heading>
            <FormLayout.Group>
              <TextField
                label="API Key"
                value={userSettings.gemini.apiKey}
                onChange={(value) => setUserSettings(prev => ({
                  ...prev,
                  gemini: {
                    ...prev.gemini,
                    apiKey: value
                  }
                }))}
                type="password"
                autoComplete="off"
                helpText="Your Google Gemini API key"
              />
            </FormLayout.Group>
            
            <TextField
              label="Base URL"
              value={userSettings.gemini.baseUrl}
              onChange={(value) => setUserSettings(prev => ({
                ...prev,
                gemini: {
                  ...prev.gemini,
                  baseUrl: value
                }
              }))}
              autoComplete="off"
              helpText="Base URL for Gemini API requests (default: https://generativelanguage.googleapis.com/v1beta)"
            />
            
            <Heading>Anthropic Claude</Heading>
            <FormLayout.Group>
              <TextField
                label="API Key"
                value={userSettings.claude?.apiKey || ''}
                onChange={(value) => setUserSettings(prev => ({
                  ...prev,
                  claude: {
                    ...prev.claude,
                    apiKey: value
                  }
                }))}
                type="password"
                autoComplete="off"
                helpText="Your Anthropic Claude API key"
              />
            </FormLayout.Group>
            
            <TextField
              label="Base URL"
              value={userSettings.claude?.baseUrl || 'https://api.anthropic.com/v1'}
              onChange={(value) => setUserSettings(prev => ({
                ...prev,
                claude: {
                  ...prev.claude,
                  baseUrl: value
                }
              }))}
              autoComplete="off"
              helpText="Base URL for Claude API requests (default: https://api.anthropic.com/v1)"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
